import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { AdminDataProvider, useAdminData } from './contexts/AdminDataContext';
import { AdminShell } from './components/admin/AdminShell';
import { PublicSite } from './pages/PublicSite';
import { AdminLogin } from './pages/admin/AdminLogin';
import { Overview } from './pages/admin/Overview';
import { Enquiries } from './pages/admin/Enquiries';
import { Quotes } from './pages/admin/Quotes';
import { QuoteNew } from './pages/admin/QuoteNew';
import { QuoteDetail } from './pages/admin/QuoteDetail';
import { Cases } from './pages/admin/Cases';
import { CaseNew } from './pages/admin/CaseNew';
import { CaseDetail } from './pages/admin/CaseDetail';
import { Invoices } from './pages/admin/Invoices';
import { InvoiceDetail } from './pages/admin/InvoiceDetail';
import { Customers } from './pages/admin/Customers';
import { CustomerDetail } from './pages/admin/CustomerDetail';
import { Settings } from './pages/admin/Settings';
import { Calendar } from './pages/admin/Calendar';
import { CasesGallery } from './pages/admin/CasesGallery';
import { company } from './data/site';
import { applyBrandIcon } from './utils/favicon';
import { initAnalytics } from './utils/analytics';
import { adminThemeClass, readAdminTheme } from './hooks/useAdminTheme';
function RequireAdmin({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    loggedIn,
    authLoading
  } = useAdminData();
  if (authLoading) {
    return <div className={`${adminThemeClass(readAdminTheme())} flex min-h-screen w-full items-center justify-center bg-canvas`} data-id="element-0" data-mp="33nges1of2pye">
        <p className="flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1" data-mp="38kgocmkh6au9">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-2" data-mp="3dlns8ajhypvn" />
          Henter …
        </p>
      </div>;
  }
  if (!loggedIn) return <Navigate to="/admin/login" replace data-id="element-3" data-mp="suncq6knacg2" />;
  return <>{children}</>;
}
export function App() {
  useEffect(() => {
    applyBrandIcon(company.logoMark, 'Lykkeværk');
    void initAnalytics();
  }, []);
  return <AdminDataProvider data-id="element-4" data-mp="3ud1ftael3qb5">
      <BrowserRouter data-id="element-5" data-mp="3ubrfeek9e61c">
        <Routes data-id="element-6" data-mp="3uc9j1w993mja">
          <Route path="/" element={<PublicSite data-id="element-8" data-mp="d4o0nv3yoa99" />} data-id="element-7" data-mp="3uevsk1fpcc45" />
          <Route path="/admin/login" element={<AdminLogin data-id="element-10" data-mp="d3wcv6glvgtj" />} data-id="element-9" data-mp="d4xq9hl3gfef" />
          <Route path="/admin" element={<RequireAdmin data-id="element-12" data-mp="86cnbaegjhma">
                <AdminShell data-id="element-13" data-mp="3gle8mfj3sxdl" />
              </RequireAdmin>} data-id="element-11" data-mp="85l2mumef4as">
            <Route index element={<Overview data-id="element-15" data-mp="3bmbgfvo82ln1" />} data-id="element-14" data-mp="3gja7g5clach0" />
            <Route path="henvendelser" element={<Enquiries data-id="element-17" data-mp="3bo4n3ayu55v1" />} data-id="element-16" data-mp="3bn30c98giuxh" />
            <Route path="tilbud" element={<Quotes data-id="element-19" data-mp="3qbfvx53mubrg" />} data-id="element-18" data-mp="3qb7i9azor2f7" />
            <Route path="tilbud/ny" element={<QuoteNew data-id="element-21" data-mp="3qd0sf09ic4ry" />} data-id="element-20" data-mp="3qbzd2jlgkw4f" />
            <Route path="tilbud/:quoteId" element={<QuoteDetail data-id="element-23" data-mp="3liwjibgolmo8" />} data-id="element-22" data-mp="3ljxvqj9fof7j" />
            <Route path="sager" element={<Cases data-id="element-25" data-mp="2xudatutpz7vm" />} data-id="element-24" data-mp="3limsqlntbbhh" />
            <Route path="sager/ny" element={<CaseNew data-id="element-27" data-mp="2xt253pgprq1v" />} data-id="element-26" data-mp="2xtv7yfcjxveq" />
            <Route path="sager/:caseId" element={<CaseDetail data-id="element-29" data-mp="2suqzjqxgc1pg" />} data-id="element-28" data-mp="2svtnbpzh9o15" />
            <Route path="kalender" element={<Calendar data-id="element-31" data-mp="18drk5y5ejjev" />} data-id="element-30" data-mp="2svjxc2efngvg" />
            <Route path="cases" element={<CasesGallery data-id="element-33" data-mp="18c6q09n022hj" />} data-id="element-32" data-mp="18dhp3w5i2m2b" />
            <Route path="fakturaer" element={<Invoices data-id="element-35" data-mp="1d4rfiyyvhtf9" />} data-id="element-34" data-mp="18bydiiusgz6v" />
            <Route path="fakturaer/:invoiceId" element={<InvoiceDetail data-id="element-37" data-mp="yfljtkz8ulc4" />} data-id="element-36" data-mp="1d62iiwkhux5b" />
            <Route path="kunder" element={<Customers data-id="element-39" data-mp="yh6a2yeizi46" />} data-id="element-38" data-mp="ygd5o431h4p7" />
            <Route path="kunder/:customerId" element={<CustomerDetail data-id="element-41" data-mp="13fq0pc42di2v" />} data-id="element-40" data-mp="ydqyw6nz2t81" />
            <Route path="indstillinger" element={<Settings data-id="element-43" data-mp="1r4ioumeg2lmx" />} data-id="element-42" data-mp="13ey2rumzv89f" />
          </Route>
          <Route path="*" element={<Navigate to="/" replace data-id="element-45" data-mp="1r268gdoc4jbz" />} data-id="element-44" data-mp="1r5k99hhcokhb" />
        </Routes>
      </BrowserRouter>
    </AdminDataProvider>;
}